import type { CSSProperties } from 'react';
import { getFontFamilyCSS } from '../services/brand-settings';
import { teboxDesignSystem } from '@boubou/tebox-design-system';

const ds = teboxDesignSystem;

/**
 * Online booking defaults before club brand loads — Inter (Tebox platform UI).
 */
export const BOOKING_TEBOX_DEFAULT_FONT: string = 'Inter';
export const BOOKING_TEBOX_BODY_COLOR: string = ds.colors.text.primary;

/** Guest booking UI: Tebox scale + club font from settings (explicit stack, not only `--brand-font`). */
export const bookingFontVar = 'var(--brand-font)' as const;

/** Prefer API/font name from React state so typography matches before/after CSS vars sync. */
function bookingFace(brandFontName: string | undefined): string {
  const t = brandFontName?.trim();
  if (!t) return bookingFontVar;
  if (t.startsWith('var(')) return t;
  return getFontFamilyCSS(t);
}

/**
 * Matches {@link BrandSettingsCard} title: `typography.body` + semibold + tight line-height.
 */
export function bookingSettingsCardTitleStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const b = ds.typography.body;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: b.fontSize,
    fontWeight: 500,
    lineHeight: 1.3,
    letterSpacing: b.letterSpacing,
    color: 'var(--brand-text)',
    ...overrides,
  };
}

/**
 * Matches Typography card helper: caption + secondary text color.
 */
export function bookingHelperTextStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const c = ds.typography.caption;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: c.fontSize,
    fontWeight: c.fontWeight,
    lineHeight: 1.4,
    letterSpacing: c.letterSpacing,
    color: ds.colors.text.secondary,
    ...overrides,
  };
}

/**
 * Section labels (Tebox `formSectionLabel` scale + weight) with brand font.
 * Uses primary brand ink (`--brand-text`), not secondary/muted — matches token `formSectionLabel.color`.
 */
export function bookingFormSectionLabelStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const t = ds.typography.formSectionLabel;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: t.fontSize,
    fontWeight: t.fontWeight,
    lineHeight: t.lineHeight,
    letterSpacing: t.letterSpacing,
    textTransform: t.textTransform,
    color: 'var(--brand-text)',
    ...overrides,
  };
}

/** Primary modal headline (rate picker etc.) — Tebox **h3** for clear hierarchy vs body. */
export function bookingPricingModalTitleStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const h = ds.typography.h3;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: h.fontSize,
    fontWeight: h.fontWeight,
    lineHeight: h.lineHeight,
    letterSpacing: h.letterSpacing,
    color: 'var(--brand-text)',
    ...overrides,
  };
}

/** Modal / sheet titles — Tebox `h4` scale + brand font. */
export function bookingModalTitleStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const h = ds.typography.h4;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: h.fontSize,
    fontWeight: h.fontWeight,
    lineHeight: h.lineHeight,
    letterSpacing: h.letterSpacing,
    color: 'var(--brand-text)',
    ...overrides,
  };
}

/** Date picker panel: default rhythm (Tebox `body`) + brand font — children inherit metrics. */
export function bookingDatePickerSurfaceStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const b = ds.typography.body;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: b.fontSize,
    fontWeight: b.fontWeight,
    lineHeight: b.lineHeight,
    letterSpacing: b.letterSpacing,
    color: 'var(--brand-text)',
    ...overrides,
  };
}

/** Month/year in picker nav — Tebox `body` + semibold (not arbitrary `text-lg`). */
export function bookingCalendarNavMonthStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const b = ds.typography.body;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: b.fontSize,
    fontWeight: 500,
    lineHeight: b.lineHeight,
    letterSpacing: b.letterSpacing,
    color: 'var(--brand-text)',
    ...overrides,
  };
}

/** Weekday header row — Tebox `fieldLabelBlock` + secondary color. */
export function bookingCalendarWeekdayStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const f = ds.typography.fieldLabelBlock;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: f.fontSize,
    fontWeight: f.fontWeight,
    lineHeight: f.lineHeight,
    letterSpacing: f.letterSpacing,
    color: ds.colors.text.secondary,
    ...overrides,
  };
}

/** Day cells — Tebox `bodySmall` + medium weight (per design-system button mapping). */
export function bookingCalendarDayStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const bs = ds.typography.bodySmall;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: bs.fontSize,
    fontWeight: 500,
    lineHeight: bs.lineHeight,
    letterSpacing: bs.letterSpacing,
    ...overrides,
  };
}

/** Today / Tomorrow in picker — Tebox `body` + medium weight. */
export function bookingCalendarFooterActionStyle(
  brandFontName: string | undefined,
  overrides?: CSSProperties,
): CSSProperties {
  const b = ds.typography.body;
  return {
    fontFamily: bookingFace(brandFontName),
    fontSize: b.fontSize,
    fontWeight: 500,
    lineHeight: b.lineHeight,
    letterSpacing: b.letterSpacing,
    ...overrides,
  };
}
